import React, {PureComponent} from 'react';
import {NotificationManager} from 'react-notifications';
import {Link} from 'react-router-dom';

import * as api from '../../lib/api';
import * as actions from '../../state/actions';
import * as validation from '../../lib/validation';
import {withUser} from '../../lib/user';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Input from '../../components/Input';
import Button from '../../components/Button';
import TimeZonePicker from '../../components/TimeZonePicker';
import Notification from '../../components/Notification';
import Role from "../../lib/Role";
import ChipInput from "../../components/ChipInput";
import {T} from '../../components/I18n'

class UserFormModal extends PureComponent {
  state = this.getInitialState();

  getInitialState() {
    return {
      saving: false,
      validationEnabled: false,
      reviewerError: undefined,
      id: undefined,
      ...(this.props.user || createUser())
    };
  }

  handleUserGroupsChanged = userGroups => {
    this.setState({ groups: userGroups});
  }

  componentDidUpdate(prevProps) {
    if (prevProps.user !== this.props.user) {
      this.setState(this.getInitialState());
    }
  }

  handleSaveUser = async () => {
    const {dispatch, credentials, activeUser, onSave} = this.props;
    const {password, groups, ...user} = this.state;
    const isNewUser = !user.id;

    if (password || isNewUser) {
      user.password = password;
    }

    // Check for validation issues.
    const validations = getValidationErrors(user, {isNewUser});

    if (hasValidations(validations)) {
      this.setState({validationEnabled: true});
      return;
    }

    this.setState({saving: true});

    try {
      let response;
      user.groups = groups;
      if(user.id) {
        response = await api.updateUser(user, credentials);
      }
      else {
        response = await api.createUser(user, credentials);
      }

      if(response.id === activeUser.id) {
        // User is updating their self, so update the state store.
        dispatch(actions.setUser(response));
      }

      NotificationManager.success(<T isolate><T variable="username">{user.userName}</T> was saved</T>);
      onSave(response);
    }
    catch(e) {
      if (e.status === 409) {
        const response = await e.json();
        this.setState({reviewerError: response.body});
      }
      else {
        NotificationManager.error('Unable to save user. Please try again.');
      }
    }
    finally {
      this.setState({saving: false});
    }
  }

  render() {
    const {open, onClose, activeUser, groups} = this.props;
    const {saving, validationEnabled, reviewerError, ...user} = this.state;
    const validations = getValidationErrors(user, {
      isNewUser: !user.id,
      disabled: !validationEnabled
    });

    let userGroups = [];

    // Add a toString method to the groups that the user belongs to for their display in the chip input.
    if (user.groups !== undefined) {
      userGroups = user.groups;
      userGroups.forEach(group => group.toString = () => group.name);
    }

    groups.forEach(group => group.toString = () => group.name);


    return (
      <Form onSubmit={this.handleSaveUser}>
        <SideModal
          open={open}
          title={user.id ? 'Edit User' : 'Create User'}
          onClose={onClose}
          options={[
            <Button type="submit" disabled={saving || hasValidations(validations)}>{saving ? 'Saving...' : 'Save User'}</Button>,
            <Button secondary onClick={onClose}>Cancel</Button>
          ]}>
          {open && (
            <SideModal.Content>
              <Form.Group>
                <Input
                  type="text" 
                  id="username" 
                  label="Username" 
                  value={user.userName || ''} 
                  onChange={e => this.setState({userName: e.target.value})}
                  invalid={validations.userName}
                  validationText={validations.userName}
                />
              </Form.Group>
              <Form.Group>
                <Input
                  type="email"
                  id="email"
                  label="Email"
                  value={user.email || ''}
                  onChange={e => this.setState({email: e.target.value})}
                  invalid={validations.email}
                  validationText={validations.email}
                />
              </Form.Group>
              <Form.Group>
                <Input
                  type="password"
                  id="password"
                  label="Password"
                  placeholder={user.id && 'Leave this field blank to keep the current password'}
                  helpText="Passwords must be at least 6 characters"
                  value={user.password || ''}
                  onChange={e => this.setState({password: e.target.value})}
                  invalid={validations.password}
                  validationText={validations.password}
                />
              </Form.Group>
              <Form.Group>
                <TimeZonePicker
                  id="timezone"
                  label="Timezone"
                  selected={user.timeZone}
                  onSelect={timeZone => this.setState({timeZone})}
                />
              </Form.Group>
              <Form.Group>
                <Input
                  type="select"
                  id="role"
                  label="Role"
                  helpText={activeUser.id === user.id ? 'You cannot change your own role' : getRoleHelpText(user.role)}
                  disabled={activeUser.id === user.id}
                  invalid={reviewerError}
                  value={user.role || ''}
                  onChange={e => this.setState({role: e.target.value})}>
                  {getRoleOptions(activeUser.role)}
                </Input>
                {reviewerError && (
                  <Notification error>
                    To change this user's role, remove them as a <strong>reviewer</strong> from the following forms:
                    <ul>
                      {reviewerError.map(form => (
                        <li key={form.id}><Link to={`/forms/${form.id}`} target="_blank" rel="noreferrer">{form.name}</Link></li>
                      ))}
                    </ul>
                  </Notification>
                )}
              </Form.Group>
              <Form.Group>
                <ChipInput
                    id="group-members"
                    placeholder="Enter a user group"
                    helpText="Select the groups this user will belong to"
                    label="User Groups"
                    value={userGroups}
                    suggestions={groups}
                    onChange={this.handleUserGroupsChanged}
                />
              </Form.Group>
            </SideModal.Content>
          )}
        </SideModal>
      </Form>
    );
  }
}

function createUser() {
  return {
    userName: '',
    password: '',
    email: '',
    groups: [],
    role: Role.USER,
    timeZone: 'Etc/UTC'
  };
}

function getRoleOptions(role) {
  const options = [
    role === Role.ADMIN && {'ADMIN': 'Administrator'},
    {'MANAGER': 'Manager'},
    {'REVIEWER': 'Reviewer'},
    {'ANALYST': 'Analyst'},
    {'USER': 'User'}
  ].filter(Boolean);

  return options.map((item, index) =>
      <option key={index} value={Object.keys(item)[0]}>{Object.values(item)[0]}</option>);
}

function getRoleHelpText(role) {
  switch (role) {
    case Role.ADMIN:
      return 'Administrators have full read/write system access';
    case Role.REVIEWER:
      return 'Reviewers can view the dashboard to approve or reject form submissions';
    case Role.MANAGER:
      return 'Managers have partial read/write system access';
    case Role.ANALYST:
      return 'Analysts have read-only access to the dashboard';
    default:
      return 'Users have no dashboard access';
  }
}

const isValuePresent = validation.create((value, {isEmpty, trim}) => !isEmpty(trim(value)));
const isValidEmail = validation.create((email, {isEmail}) => isEmail(email));
const isValidPassword = validation.create((password, {trim}) => trim(password).length >= 6);

function getValidationErrors(user, options = {disabled: false, isNewUser: false}) {
  if (options.disabled) {
    return {};
  }
  
  return {
    userName: isValuePresent(user.userName) ? null : 'A username is required',
    email: isValidEmail(user.email) ? null : 'A valid email address is required',
    password: (!user.password && !options.isNewUser) || isValidPassword(user.password) ? null : 'Passwords must be at least 6 characters'
  };
}

function hasValidations(validations) {
  return Object.keys(validations).map(key => validations[key]).filter(Boolean).length > 0;
}

export default withUser(UserFormModal, {userProp: 'activeUser'});
